import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'

import {
  getLoginSerialNumber,
  LoginSerialNumberStatus,
  pollLoginSerialNumberStatus,
  pollLoginSerialNumberStatusVariables,
} from '~/graphqls/types'
import classNames from 'classnames'
import {
  GET_LOGIN_SERIAL_NUMBER,
  POLL_LOGIN_SERIAL_NUMBER_STATUS,
} from '~/graphqls/gqls'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styles from './QRLogin.module.css'
import { crossLogin } from '~/utils'
import Cookies from 'js-cookie'
import { CDNImage } from '~/components/business/CDNImage'
import { MyIcon } from '~/components/ui/MyIcon'
import { useDeviceInfo } from '~/hooks/useDeviceInfo'

export const QRLogin: React.FC<{
  setVisible?: (visible: boolean) => void
  backUrl?: string
}> = ({ setVisible, backUrl }) => {
  const [serialNumber, setSerialNumber] = useState<string | null>()
  const { handleSendDeviceInfo } = useDeviceInfo()
  const [qrStatus, setQrStatus] = useState<
    'networkError' | LoginSerialNumberStatus
  >(LoginSerialNumberStatus.NO_LOGIN)

  const getLoginSerialNumberAction = useImperativeQuery<getLoginSerialNumber>(
    GET_LOGIN_SERIAL_NUMBER
  )

  const pollLoginSerialNumberStatusAction = useImperativeQuery<
    pollLoginSerialNumberStatus,
    pollLoginSerialNumberStatusVariables
  >(POLL_LOGIN_SERIAL_NUMBER_STATUS)

  const handleLoginSerialNumber = async () => {
    try {
      const res = await getLoginSerialNumberAction()
      setSerialNumber(res.data.loginSerialNumber?.serialNumber)
      setQrStatus(LoginSerialNumberStatus.NO_LOGIN)
    } catch (err) {
      setQrStatus('networkError')
    }
  }

  useEffect(() => {
    handleLoginSerialNumber()
  }, [])

  const pollTimer = useRef(0)
  useEffect(() => {
    clearInterval(pollTimer.current)
    if (!serialNumber) return

    pollTimer.current = window.setInterval(async () => {
      try {
        const res = await pollLoginSerialNumberStatusAction({ serialNumber })
        const status = res.data.loginSerialNumberStatus?.status
        if (status === LoginSerialNumberStatus.LOGIN) {
          Cookies.set(
            'gate-token',
            res.data?.loginSerialNumberStatus?.token || '',
            {
              expires: 365,
            }
          )
          setVisible?.(false)
          setQrStatus(LoginSerialNumberStatus.NO_LOGIN)
          crossLogin(res.data.loginSerialNumberStatus?.tokenVoucher!, backUrl)
          handleSendDeviceInfo()
        } else {
          setQrStatus(status as LoginSerialNumberStatus)
        }
      } catch (e) {
        setQrStatus('networkError')
      }
    }, 1500)

    return () => clearInterval(pollTimer.current)
  }, [serialNumber])

  return (
    <div className="bg-neutral-200 p-4 pb-0 rounded-xl space-y-4">
      <p className="text-neutral-400">打开无界AI APP - 我的 - 扫一扫</p>
      <div className="flex justify-between mt-7">
        <div className="p-2 bg-neutral-800 rounded-lg h-[180px] relative">
          <QRCodeSVG
            size={160}
            value={JSON.stringify({
              serialNumber: serialNumber,
              action: 'login',
            })}
          ></QRCodeSVG>
          {(!serialNumber ||
            qrStatus === 'networkError' ||
            qrStatus === LoginSerialNumberStatus.EXPIRED) && (
            <div
              className={classNames(
                'cursor-pointer absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center',
                styles.qrMask
              )}
              onClick={handleLoginSerialNumber}
            >
              <span
                className="h-6 w-6 p-0.5 text-center rounded-full"
                style={{
                  background: `rgba(52, 64, 84, 0.6)`,
                }}
              >
                <MyIcon icon="refresh" className="text-[12px]" />
              </span>
              <p className="mt-2 text-center text-neutral-300 text-xs">
                {qrStatus === LoginSerialNumberStatus.EXPIRED
                  ? '二维码已失效'
                  : '二维码加载失败'}
                <br />
                点击刷新
              </p>
            </div>
          )}

          {qrStatus === LoginSerialNumberStatus.SCAN_CODE && (
            <div
              className={classNames(
                'cursor-pointer absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center',
                styles.qrMask
              )}
            >
              <span className="h-6 w-6 p-0.5 text-center rounded-full bg-[#45B36B]">
                <MyIcon icon="check" className="text-[20px]" />
              </span>
              <p className="mt-2 text-center text-neutral-300 text-xs">
                扫描成功
                <br />
                请在手机上点击确认登录
              </p>
            </div>
          )}
        </div>

        <CDNImage
          src="/images/login/scan-qr-1@2x.png"
          alt=""
          className="w-[144px] h-[198px] block"
        />
      </div>
    </div>
  )
}
